import { Grid } from "@mui/material";
import { useGetTransactionList } from "../services/AnalyticsPageService";
import storage from "services/storage";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OverflowText } from "components";
import { formatDate } from "utils/formatDate";
import { formatNumber } from "utils/formatNumber";
import { capitalize } from "lodash";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";
import dayjs from "dayjs";
import InputDateRangePickerComponent from "components/input-date-range-picker/InputDateRangePickerComponent";

const LatestTransactionList = ({
  defaultPeriodStart,
  defaultPeriodEnd,
  closePriceResp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const companyIdLocalStorage = storage.getCompanyID();
  const { response } = useGetTransactionList({
    id: companyIdLocalStorage,
    defaultPeriodStart: formatDate.toAPIDate(defaultPeriodStart),
    defaultPeriodEnd: formatDate.toAPIDate(defaultPeriodEnd),
  });
  const data = useMemo(() => response?.data?.transactions ?? [], [response]);

  const handleOnSelectDate = useCallback(
    (payload) => {
      const stringifiedParams = paramStringify({
        ...queryParams,
        period_start: formatDate.toAPIDate(payload?.value?.dateStart),
        period_end: formatDate.toAPIDate(payload?.value?.dateEnd),
      });
      navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
        replace: true,
      });
    },
    [location?.pathname, navigate, queryParams],
  );

  const handleToTransactionsPage = useCallback(() => {
    const periodStart =
      queryParams?.period_start || closePriceResp?.data?.start_date;
    const periodEnd = queryParams?.period_end || closePriceResp?.data?.end_date;

    const stringifiedParams = paramStringify({
      period_start: formatDate.toAPIDate(periodStart),
      period_end: formatDate.toAPIDate(periodEnd),
    });
    navigate(
      generateUrlWithParams("/analytics/transactions", stringifiedParams),
      {
        replace: true,
      },
    );
  }, [
    closePriceResp?.data?.end_date,
    closePriceResp?.data?.start_date,
    navigate,
    queryParams?.period_end,
    queryParams?.period_start,
  ]);

  return (
    <div className='section'>
      <div className='flex justify-between'>
        <p className='font-bold text-lg mb-6'>
          Latest Transactions by Affiliated Shareholders
        </p>
        <InputDateRangePickerComponent
          valueDateStart={dayjs(
            queryParams?.period_start || closePriceResp?.data?.start_date,
          )}
          valueDateEnd={dayjs(
            queryParams?.period_end || closePriceResp?.data?.end_date,
          )}
          maxEndDate={6}
          onSelect={handleOnSelectDate}
          disableWeekends={true}
          limitToFiveYears={true}
        />
      </div>
      {Boolean(data?.length) ? (
        <Grid container spacing={2}>
          {data?.map((el) => (
            <Grid item xs={4}>
              <div
                className='section'
                style={{
                  backgroundImage: `linear-gradient(to right, #FFFFFF, ${
                    el?.transaction_type === "BUY" ? "#E6F5EF80" : "#FCEAED80"
                  })`,
                }}
              >
                <OverflowText textClassName='mb-4 font-bold' text={el?.name} />
                <div className='flex flex-row justify-between'>
                  <div className='flex flex-row gap-4'>
                    <div className='flex flex-col gap-3'>
                      <p className='text-sm text-[#5F737F]'>SID</p>
                      <p className='text-sm text-[#5F737F]'>Date</p>
                    </div>

                    <div className='flex flex-col gap-3'>
                      <p className='text-sm'>{el?.sid}</p>
                      <p className='text-sm'>
                        {formatDate?.toShortDate(el?.date)}
                      </p>
                    </div>
                  </div>
                  <div className='flex flex-col gap-2'>
                    <p className='text-sm font-bold'>
                      {formatNumber?.toAmount(el?.shares_amount)}
                    </p>
                    <div
                      className={`rounded-full px-2 py-1 text-xs max-w-[36px] ${
                        el?.transaction_type === "BUY"
                          ? "bg-[#E6F5EF] text-[#039E5A]"
                          : "bg-[#FCEAED] text-[#E52A49]"
                      }`}
                    >
                      {capitalize(el?.transaction_type)}
                    </div>
                  </div>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <img
          alt='empty-icon'
          src='/images/empty-latest-transaction.svg'
          className='m-auto'
        />
      )}

      <p
        onClick={handleToTransactionsPage}
        className='cursor-pointer font-extrabold text-[#556FB5] text-xs text-center mt-4'
      >
        View All
      </p>
    </div>
  );
};

export default LatestTransactionList;
